import { apiClient } from './api'

export interface CreditPack {
  id: string
  name: string
  description: string
  cost: number
  cost_dollars: number
  credits: number
  per_credit_cost: number
  price_id: string
}

export interface CreditBalance {
  credit_balance: number
  max_users: number
  has_payment_method: boolean
}

interface CheckoutSession {
  clientSecret: string
  sessionId: string
  purchaseId: string
}

class CheckoutService {
  // Get current credit balance
  async getBalance(): Promise<CreditBalance> {
    const response = await apiClient.request<CreditBalance>('get', '/credits/balance')
    return response as unknown as CreditBalance
  }

  // Get available credit packs
  async getPacks(): Promise<CreditPack[]> {
    try {
      const response = await apiClient.request<{ packs: CreditPack[] }>('get', '/credits/packs')
      return (response as any).packs ?? []
    } catch (error) {
      return []
    }
  }

  // Create a checkout session for a credit pack
  async createCheckoutSession(creditPackId: string): Promise<CheckoutSession> {
    const response = await apiClient.request<CheckoutSession>('post', '/credits/checkout', {
      credit_pack_id: creditPackId
    })
    return response as unknown as CheckoutSession
  }

  // Initialize Stripe and create checkout session
  async initializeStripeCheckout(creditPackId: string, containerId: string): Promise<any> {
    const { loadStripe } = await import('@stripe/stripe-js')

    const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
    if (!stripePublishableKey) {
      throw new Error('Stripe publishable key not configured')
    }

    const stripe = await loadStripe(stripePublishableKey)
    if (!stripe) {
      throw new Error('Failed to load Stripe')
    }

    const session = await this.createCheckoutSession(creditPackId)

    const checkout = await stripe.initEmbeddedCheckout({
      clientSecret: session.clientSecret,
    })

    checkout.mount(containerId)

    return {
      stripe,
      checkout,
      session
    }
  }

  // Create a SetupIntent for saving a payment method without charging
  async createSetupIntent(): Promise<{ client_secret: string }> {
    const response = await apiClient.request('post', '/credits/setup_intent')
    return response as unknown as { client_secret: string }
  }

  // Verify a completed checkout session and fulfill credits
  async verifySession(sessionId: string): Promise<{ credit_balance: number; credits_added?: number }> {
    const response = await apiClient.request('post', '/credits/verify', { session_id: sessionId })
    return response as unknown as { credit_balance: number; credits_added?: number }
  }

  // Format price for display
  formatPrice(cents: number, currency: string = 'USD'): string {
    const dollars = cents / 100
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(dollars)
  }
}

export const checkoutService = new CheckoutService()
